import { IntegrationServiceDataMap } from '@/app/settings/organization/integrations/utils';
import type { IdentifierMapping } from '..';
import {
  getContractMapping,
  getInvoiceMapping,
  getObjectsMapping,
  getTenantMapping,
  getVacancyMapping,
} from './utils';
import {
  GENERAL_ERRAND_ACCEPTED_STATUSES,
  GENERAL_ERRAND_COMPLETED_STATUSES,
  INSPECTION_ERRAND_ACCEPTED_STATUSES,
  INSPECTION_ERRAND_COMPLETED_STATUSES,
} from '@/app/dashboard/technical-management/constants';

const now = new Date().toISOString();

export const getToHandleIdentifierData: (
  userId: string | null
) => IdentifierMapping[string] = (userId) => ({
  apartment_contracts: getContractMapping('apartmentcontract'),
  industrialpremises_contracts: getContractMapping(
    'industrialpremisescontract'
  ),
  parkingspot_contracts: getContractMapping('parkingspotcontract'),
  outdoorsection_contracts: getContractMapping('outdoorsectioncontract'),
  other_contracts: getContractMapping('othercontract'),
  block_contracts: getContractMapping('blockcontract'),
  organization_user: {
    active_users: {
      modelName: 'organizationuser',
      filters: {
        is_active: {
          noop: true,
        },
        last_login: { __isnull: false },
      },
    },
    invited_users: {
      modelName: 'organizationuser',
      filters: {
        is_active: {
          noop: false,
        },
        hotp_invite_sendout_time: { __isnull: false },
      },
    },
    inactivated_users: {
      modelName: 'organizationuser',
      filters: {
        is_active: {
          noop: false,
        },
      },
    },
  },
  tenant: getTenantMapping('tenant'),
  brf_member: getTenantMapping('tenant', true),
  vacancy_objects: getVacancyMapping(),
  // TODO: Are these needed anymore?
  apartment: getObjectsMapping(),
  industrial_premises: getObjectsMapping(),
  outdoor_section: getObjectsMapping(),
  parking_spot: getObjectsMapping(),
  common_area: getObjectsMapping(),
  brf_apartment: getObjectsMapping(),
  invoice: getInvoiceMapping('invoice'),
  deliveries: {
    invoice_sendout_failed: {
      overrideUrl: true,
    },
  },
  non_rental_invoice: getInvoiceMapping('invoice', true),
  invoice_payment: {
    non_matched_payments: {
      modelName: 'invoicepayment',
      filters: {
        is_unmatched: {
          noop: true,
        },
        annulled: {
          noop: false,
        },
      },
    },
    overpaid_payments: {
      modelName: 'invoicepayment',
      filters: {
        is_overpayment: {
          noop: true,
        },
        annulled: {
          noop: false,
        },
      },
    },
    unhandled_payments: {
      modelName: 'invoicepayment',
      filters: {
        is_unhandled: {
          noop: true,
        },
        annulled: {
          noop: false,
        },
      },
    },
  },
  imd: {
    not_matched_sensors: {
      modelName: 'sensor',
      filters: {
        apartment: { __isnull: true },
        common_area: { __isnull: true },
        industrial_premises: { __isnull: true },
        parking_spot: { __isnull: true },
        brf_apartment: { __isnull: true },
      },
    },
  },
  monitoring: {
    pending_approvals: {
      modelName: 'monitoringconfiguration',
      noFilterReturn: true,
      filters: {
        is_relevant_for_approving: { noop: true },
        mt_approved_time: {
          __isnull: true,
        },
        mt_denied_time: {
          __isnull: true,
        },
      },
    },
    retro_activity_proposals: {
      group_modelNames: [
        'retroactiveapartmentrevenueproposal',
        'retroactiveindustrialpremisesrevenueproposal',
        'retroactiveparkingspotrevenueproposal',
        'retroactiveoutdoorsectionrevenueproposal',
      ],
      filters: {
        accepted: { __isnull: true },
      },
    },
    distribution_quota_proposals: {
      modelName: 'automaticdistributionquota',
      filters: {
        accepted: { __isnull: true },
        hidden: { noop: false },
        apartment: { '__isnull!': true },
        industrial_premises: { '__isnull!': true },
        outdoor_section: { '__isnull!': true },
        parking_spot: { '__isnull!': true },
        brf_apartment: { '__isnull!': true },
      },
    },
  },
  brf: {
    has_no_listed_control_tasks: {
      modelName: 'brfapartment',
      filters: {},
    },
    has_no_active_invoicing_configuration: {
      modelName: 'brfapartment',
      filters: {
        has_active_invoicing_configuration: { noop: false },
      },
    },
    has_no_indefinite_invoicing_configuration: {
      modelName: 'brfapartment',
      filters: {
        has_indefinite_invoicing_configuration: { noop: false },
      },
    },
  },
  bookkeeping: {
    verification_groups_to_attest: {
      modelName: 'verificationgroup',
      filters: {
        confirmation_date: { __isnull: true },
      },
    },
    verification_not_exported_fortnox: {
      modelName: 'verification',
      filters: {
        exported_for_import_in_file: { __isnull: true },
        verification_group: { __isnull: false },
        integration_applications_not_connected: {
          noop: IntegrationServiceDataMap.FORTNOX?.applicationId,
        },
      },
    },
    verification_not_exported_visma: {
      modelName: 'verification',
      filters: {
        exported_for_import_in_file: { __isnull: true },
        verification_group: { __isnull: false },
        integration_applications_not_connected: {
          noop: IntegrationServiceDataMap.VISMA_EEKONOMI?.applicationId,
        },
      },
    },
  },
  errands: {
    not_started_errands: {
      modelName: 'generalerrand',
      filters: {
        status: {
          noop: 0,
        },
      },
    },
    active_errands: {
      modelName: 'generalerrand',
      filters: {
        actual_done_time: { __isnull: true },
      },
    },
    overdue_errands: {
      modelName: 'generalerrand',
      filters: {
        actual_done_time: { __isnull: true },
        planned_done_time: {
          __lt: now,
        },
      },
    },
    waiting_for_invoice_details: {
      modelName: 'generalerrand',
      filters: {
        invoicing_status: {
          noop: 1,
        },
      },
    },
    my_accepted_errands: {
      modelName: 'generalerrand',
      filters: {
        users_accepted_delegations: {
          noop: userId ? [userId] : undefined,
        },
        status: {
          __in: [GENERAL_ERRAND_ACCEPTED_STATUSES.join(',')],
        },
      },
    },
    my_unanswered_errands: {
      modelName: 'generalerrand',
      filters: {
        users_unanswered_delegations: {
          noop: userId ? [userId] : undefined,
        },
        status: {
          '__in!': [GENERAL_ERRAND_COMPLETED_STATUSES.join(',')],
        },
      },
    },
    my_denied_errands: {
      modelName: 'generalerrand',
      filters: {
        users_denied_delegations: {
          noop: userId ? [userId] : undefined,
        },
      },
    },
    my_completed_errands: {
      modelName: 'generalerrand',
      filters: {
        users_accepted_delegations: {
          noop: userId ? [userId] : undefined,
        },
        status: {
          __in: [GENERAL_ERRAND_COMPLETED_STATUSES.join(',')],
        },
      },
    },
    my_accepted_errands_planner: {
      modelName: 'generalerrand',
      filters: {
        users_accepted_delegations_planner: {
          noop: userId ? [userId] : undefined,
        },
        status: {
          __in: [GENERAL_ERRAND_ACCEPTED_STATUSES.join(',')],
        },
      },
    },
    my_completed_errands_planner: {
      modelName: 'generalerrand',
      filters: {
        users_accepted_delegations_planner: {
          noop: userId ? [userId] : undefined,
        },
        status: {
          __in: [GENERAL_ERRAND_COMPLETED_STATUSES.join(',')],
        },
      },
    },
    waiting_for_invoice: {
      modelName: 'generalerrand',
      filters: {
        invoicing_status: {
          noop: 2,
        },
      },
    },
    waiting_for_invoice_attestation: {
      modelName: 'generalerrand',
      filters: {
        invoicing_status: {
          noop: 3,
        },
      },
    },
    waiting_for_costs: {
      modelName: 'generalerrand',
      filters: {
        invoicing_status: {
          noop: 0,
        },
      },
    },
  },
  inspections: {
    my_accepted_errands: {
      modelName: 'inspectionerrand',
      filters: {
        users_accepted_delegations: {
          noop: userId ? [userId] : undefined,
        },
        status: {
          __in: INSPECTION_ERRAND_ACCEPTED_STATUSES.join(','),
        },
      },
    },
    my_unanswered_errands: {
      modelName: 'inspectionerrand',
      filters: {
        users_unanswered_delegations: {
          noop: userId ? [userId] : undefined,
        },
        status: {
          '__in!': INSPECTION_ERRAND_COMPLETED_STATUSES.join(','),
        },
      },
    },
    not_started_errands: {
      modelName: 'inspectionerrand',
      filters: {
        status: {
          noop: 0,
        },
      },
    },
    active_errands: {
      modelName: 'inspectionerrand',
      filters: {
        actual_done_time: { __isnull: true },
      },
    },
    overdue_errands: {
      modelName: 'inspectionerrand',
      filters: {
        actual_done_time: { __isnull: true },
        planned_done_time: {
          __lt: now,
        },
      },
    },
    waiting_for_invoice_details: {
      modelName: 'inspectionerrand',
      filters: {
        invoicing_status: {
          noop: 1,
        },
      },
    },
    waiting_for_invoice: {
      modelName: 'inspectionerrand',
      filters: {
        invoicing_status: {
          noop: 2,
        },
      },
    },
    waiting_for_invoice_attestation: {
      modelName: 'inspectionerrand',
      filters: {
        invoicing_status: {
          noop: 3,
        },
      },
    },
    waiting_for_costs: {
      modelName: 'inspectionerrand',
      filters: {
        invoicing_status: {
          noop: 0,
        },
      },
    },
  },
});
